/**
 * Перенос `lib/src/utils/extensions/color_extension.dart`.
 *
 * Во Flutter расширение висело на `Color` с каналами 0..255. В вебе цвет —
 * это CSS-строка, поэтому каждая функция разбирает строку, считает ровно по
 * формулам Dart-версии (округляется приращение канала, а не сумма) и
 * собирает строку обратно.
 *
 * Формат результата: `#rrggbb`, пока альфа равна 1, иначе `rgba(r, g, b, a)` —
 * так значения из `AppColors` (hex) остаются hex, а из `whiteAlpha`/`blackAlpha`
 * (rgba) остаются rgba.
 */

const MAX_CHANNEL = 255;

/** Коэффициенты яркости из Dart-версии (ITU-R BT.601). */
const LUMA_RED = 0.299;
const LUMA_GREEN = 0.587;
const LUMA_BLUE = 0.114;

/** Порог для определения светлого или темного цвета. */
const BRIGHTNESS_THRESHOLD = 0.5;

/** `factor ?? 0.2` — менять яркость на 20% по умолчанию. */
const DEFAULT_BRIGHTNESS_FACTOR = 0.2;

const HEX_DIGITS = /^[0-9a-f]+$/i;
const RGB_FUNCTION = /^rgba?\(([^)]*)\)$/i;
const RGB_SEPARATORS = /[\s,/]+/;

export interface Rgba {
  readonly red: number;
  readonly green: number;
  readonly blue: number;
  /** 0..1, как в CSS. */
  readonly alpha: number;
}

const TRANSPARENT: Rgba = { red: 0, green: 0, blue: 0, alpha: 0 };

function clampChannel(value: number): number {
  return Math.min(Math.max(value, 0), MAX_CHANNEL);
}

function parseHexChannels(value: string): Rgba {
  const channel = (from: number): number =>
    Number.parseInt(value.substring(from, from + 2), 16);
  return {
    red: channel(0),
    green: channel(2),
    blue: channel(4),
    alpha: value.length === 8 ? channel(6) / MAX_CHANNEL : 1,
  };
}

/** `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`. */
function parseHex(color: string): Rgba | null {
  const value = color.substring(1);
  if (!HEX_DIGITS.test(value)) return null;
  if (value.length === 3 || value.length === 4) {
    const expanded = value
      .split('')
      .map((digit) => digit + digit)
      .join('');
    return parseHexChannels(expanded);
  }
  if (value.length === 6 || value.length === 8) return parseHexChannels(value);
  return null;
}

function parseChannelToken(token: string): number {
  const number = Number.parseFloat(token);
  const value = token.endsWith('%') ? (number / 100) * MAX_CHANNEL : number;
  return Math.round(value);
}

function parseAlphaToken(token: string): number {
  const number = Number.parseFloat(token);
  return token.endsWith('%') ? number / 100 : number;
}

/** `rgb(r, g, b)`, `rgba(r, g, b, a)`, `rgb(r g b / a)`. */
function parseRgbFunction(color: string): Rgba | null {
  const match = RGB_FUNCTION.exec(color);
  if (match === null) return null;

  const tokens = match[1].split(RGB_SEPARATORS).filter((token) => token.length > 0);
  if (tokens.length < 3 || tokens.length > 4) return null;

  const red = parseChannelToken(tokens[0]);
  const green = parseChannelToken(tokens[1]);
  const blue = parseChannelToken(tokens[2]);
  const alpha = tokens.length === 4 ? parseAlphaToken(tokens[3]) : 1;
  if ([red, green, blue, alpha].some(Number.isNaN)) return null;

  return {
    red: clampChannel(red),
    green: clampChannel(green),
    blue: clampChannel(blue),
    alpha: Math.min(Math.max(alpha, 0), 1),
  };
}

/** Разбор CSS-цвета. Непонятный формат — ошибка, а не молчаливый чёрный. */
export function parseCssColor(color: string): Rgba {
  const text = color.trim().toLowerCase();
  if (text === 'transparent') return TRANSPARENT;

  const parsed = text.startsWith('#') ? parseHex(text) : parseRgbFunction(text);
  if (parsed === null) {
    throw new Error(`Не удалось разобрать CSS-цвет: "${color}"`);
  }
  return parsed;
}

function toHexPair(value: number): string {
  return value.toString(16).padStart(2, '0');
}

/** Обратная сборка строки: hex при непрозрачном цвете, иначе `rgba(...)`. */
export function formatCssColor({ red, green, blue, alpha }: Rgba): string {
  if (alpha >= 1) return `#${toHexPair(red)}${toHexPair(green)}${toHexPair(blue)}`;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** Перенос `assert(factor >= 0 && factor <= 1, …)` из Dart-версии. */
function assertFactor(factor: number): void {
  if (!(factor >= 0 && factor <= 1)) {
    throw new Error('Factor must be between 0 and 1');
  }
}

export function darkenColor(color: string, factor: number): string {
  assertFactor(factor);
  const { red, green, blue, alpha } = parseCssColor(color);
  return formatCssColor({
    red: Math.round(red * (1 - factor)),
    green: Math.round(green * (1 - factor)),
    blue: Math.round(blue * (1 - factor)),
    alpha,
  });
}

export function lightenColor(color: string, factor: number): string {
  assertFactor(factor);
  const { red, green, blue, alpha } = parseCssColor(color);
  // Округляется именно приращение канала — как в Dart-версии.
  return formatCssColor({
    red: red + Math.round((MAX_CHANNEL - red) * factor),
    green: green + Math.round((MAX_CHANNEL - green) * factor),
    blue: blue + Math.round((MAX_CHANNEL - blue) * factor),
    alpha,
  });
}

/**
 * Подгоняет яркость: светлый цвет темнеет, тёмный светлеет. Так наведение
 * и нажатие видно на любой подложке.
 */
export function adjustColorBrightness(color: string, factor?: number): string {
  const { red, green, blue } = parseCssColor(color);

  // Определяем яркость цвета
  const brightness = (LUMA_RED * red + LUMA_GREEN * green + LUMA_BLUE * blue) / MAX_CHANNEL;

  // Если цвет светлый, уменьшаем яркость, иначе увеличиваем
  if (brightness > BRIGHTNESS_THRESHOLD) {
    return darkenColor(color, factor ?? DEFAULT_BRIGHTNESS_FACTOR);
  }
  return lightenColor(color, factor ?? DEFAULT_BRIGHTNESS_FACTOR);
}

/** Умножает каналы на [factor] — прямое затемнение без учёта альфы. */
export function transformColor(color: string, factor: number): string {
  // Убедимся, что фактор находится в пределах от 0 до 1
  const clamped = Math.min(Math.max(factor, 0), 1);
  const { red, green, blue, alpha } = parseCssColor(color);
  return formatCssColor({
    red: clampChannel(Math.round(red * clamped)),
    green: clampChannel(Math.round(green * clamped)),
    blue: clampChannel(Math.round(blue * clamped)),
    alpha,
  });
}
